import { clamp, cloneDeep, difference, isNumber, pullAll } from 'lodash';
import type { SizeUnitType } from '@/utils/unit-kit';
import type { IExtraInfo, ILayerProps, RenderMode } from '@/version-types';
import type { IHistoryItem } from '../history/type';
import type { ServiceManager } from '../service-manager';

export interface IAdjustUnit {
    targetUnit: SizeUnitType;
    destUnit: SizeUnitType;
}

export interface IStageHistoryBaseData {
    oldIds: number[];
    oldIndex: number;
    newIds: number[];
    newIndex: number;
    stageTypes?: { oldStageType: RenderMode; newStageType: RenderMode };
    /** 进入编辑器，只是替换模版，不需要触发自动保存*/
    _isReplaceStage?: boolean;
    /** 替换所有画布 */
    isReplaceActive?: boolean;
    /** 插入数据 */
    insertData?: {
        /** 多人插入唯一id */
        insertId: string;
        originIndexs: number[];
        extraInfos: IExtraInfo[];
        stages: ILayerProps[][];
    };
    /** 删除数据 */
    delData?: number[];
}

export interface IStageHistoryAdjustData {
    originIndex: number;
    units: IAdjustUnit;
    oldStages: ILayerProps[][];
    newStages: ILayerProps[][];
}

export interface IStageHistoryReplaceLayerData {
    /** 替换前激活的id */
    activeId?: string;
    /** 替换前包裹的id */
    wrapId?: string;
    /** 替换前的图层ids */
    ids: string[];
    /** 替换前的新图层数据 */
    newLayers: ILayerProps | ILayerProps[];
    /** 替换后的新图层ids */
    newIds: string[];
    /** 替换前的图层索引信息 */
    restoreData: { index: number; layer: ILayerProps }[];
    /** 额外更新图层 ,不写进历史记录*/
    extraUpdateLayer?: {
        oldLayers: ILayerProps[];
        newLayers: ILayerProps[];
    };
}

export const historyBaseType = 'stage-base-operate';
export const historyAdjustType = 'stage-adjust-operate';
export const historyReplaceLayerType = 'stage-replace-layer';
export const historyQuickAdjustOperate = 'quick-adjust-operate';
export const historyQuickResizeOperate = 'quick-resize-operate';

export class StageHistory {
    constructor(readonly service: ServiceManager) {
        // 注册历史记录
        this.registerBaseHistory();
        this.registerAdjustHistory();
        this.registerReplaceLayerHistory();
    }

    /** 替换图层 */
    private registerReplaceLayerHistory() {
        this.service.history.register(historyReplaceLayerType, {
            owner: 'framework/stage',
            collaboration: 'supported',
            onRedoUndo: (
                historyItem: IHistoryItem<IStageHistoryReplaceLayerData>,
                isRedo: boolean,
                isUndo: boolean,
            ) => {
                if (historyItem.isDirty) return;
                const { ids, newIds, newLayers, restoreData, activeId, wrapId, extraUpdateLayer } =
                    historyItem.data.data;
                this.service.frame.delActiveLayer();
                if (isUndo) {
                    if (extraUpdateLayer) {
                        this.service.frame.batchUpdateLayer(extraUpdateLayer.oldLayers, true);
                    }
                    this.service.frame.replaceLayer(newIds, undefined, restoreData);
                }
                if (isRedo) {
                    if (extraUpdateLayer) {
                        this.service.frame.batchUpdateLayer(extraUpdateLayer.newLayers, true);
                    }
                    this.service.frame.replaceLayer(ids, newLayers);
                }
                [activeId, wrapId].forEach((id) => {
                    if (id && this.service.flatLayer.getLayer(id)) {
                        this.service.frame.setActiveLayer(id);
                    }
                });
            },
            onAfterWrite: (historyItem: IHistoryItem<IStageHistoryReplaceLayerData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IStageHistoryReplaceLayerData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IStageHistoryReplaceLayerData>, isRedo, isUndo, store) => {
                const { ids, newIds, newLayers, restoreData } = historyItem.data.data;
                if (isUndo) {
                    const layers = Array.isArray(newLayers) ? newLayers : [newLayers];
                    for (const originLayer of layers) {
                        historyItem.isDirty = this.service.flatLayer.checkLayerIsDirty({
                            originLayer,
                            latestIsExtraGroup: this.service.flatLayer.checkIsExtraGroup(originLayer),
                            latestIsFormalGroup: this.service.flatLayer.checkIsFormalGroup(originLayer),
                        });
                        if (historyItem.isDirty) break;
                    }
                    if (!historyItem.isDirty) {
                        for (const id of newIds) {
                            historyItem.isDirty = !this.service.flatLayer.getLayer(id);
                            if (historyItem.isDirty) break;
                        }
                    }
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        for (const item of restoreData) {
                            const { layer: originLayer } = item;
                            historyItem.isDirty = this.service.flatLayer.checkLayerIsDirty({
                                originLayer,
                                latestIsExtraGroup: this.service.flatLayer.checkIsExtraGroup(originLayer),
                                latestIsFormalGroup: this.service.flatLayer.checkIsFormalGroup(originLayer),
                            });
                            if (historyItem.isDirty) break;
                        }
                        if (!historyItem.isDirty) {
                            for (const id of ids) {
                                historyItem.isDirty = !this.service.flatLayer.getLayer(id);
                                if (historyItem.isDirty) break;
                            }
                        }
                    }
                }
            },
            onMessage: (message: [IStageHistoryReplaceLayerData], isRedo, isUndo, isWrite) => {
                const { ids, newIds, newLayers, restoreData, extraUpdateLayer } = message[0];
                this.service.frame.delActiveLayer();
                if (isUndo) {
                    if (extraUpdateLayer) {
                        this.service.frame.batchUpdateLayer(extraUpdateLayer.oldLayers, true);
                    }
                    this.service.frame.replaceLayer(newIds, undefined, restoreData);
                }
                if (isRedo || isWrite) {
                    if (extraUpdateLayer) {
                        this.service.frame.batchUpdateLayer(extraUpdateLayer.newLayers, true);
                    }
                    this.service.frame.replaceLayer(ids, newLayers);
                }
            },
        });
    }

    /** 舞台调整历史, resize, scale */
    private registerAdjustHistory() {
        this.service.history.register(historyAdjustType, {
            owner: 'framework/stage',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IStageHistoryAdjustData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { originIndex, units, oldStages, newStages } = historyItem.data.data;
                this.service.frame.delActiveLayer();
                const update = (unit: SizeUnitType, stages: ILayerProps[][]) => {
                    for (let i = 0; i < stages.length; i++) {
                        this.service.frame.batchUpdateLayer(stages[i], false);
                    }
                    // this.service.frame.rerender(originIndex);
                    this.service.editor.setUnit(unit);
                    const stageIndex = this.service.frame.frameIdToIndex(originIndex);
                    this.service.frame.setActiveIndex(stageIndex);
                    // todo zp
                    // 舞台滚动由调用方在操作完成后处理。
                };
                if (isUndo) {
                    update(units.targetUnit, oldStages);
                }
                if (isRedo) {
                    update(units.destUnit, newStages);
                }
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IStageHistoryAdjustData>, isRedo, isUndo, store) => {
                const { oldStages, newStages } = historyItem.data.data;
                if (isUndo) {
                    for (let i = 0; i < newStages.length; i++) {
                        if (!historyItem.isDirty) {
                            const layers = newStages[i];
                            const oldSize = { width: layers[0]?.width || 0, height: layers[0]?.height || 0 };
                            const originIndex = this.service.frame.getLayerFrameId(layers[0].id);
                            if (!isNumber(originIndex)) {
                                historyItem.isDirty = true;
                                break;
                            }
                            const isRender = this.service.frame.checkFrameIsRender(originIndex);
                            const size = this.service.frame.getSize(originIndex);
                            historyItem.isDirty =
                                !isRender || !(size.width === oldSize.width && size.height === oldSize.height);
                        }
                        if (historyItem.isDirty) break;
                    }
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        for (let i = 0; i < oldStages.length; i++) {
                            if (!historyItem.isDirty) {
                                const layers = oldStages[i];
                                const oldSize = { width: layers[0]?.width || 0, height: layers[0]?.height || 0 };
                                const originIndex = this.service.frame.getLayerFrameId(layers[0].id);
                                if (!isNumber(originIndex)) {
                                    historyItem.isDirty = true;
                                    break;
                                }
                                const isRender = this.service.frame.checkFrameIsRender(originIndex);
                                const size = this.service.frame.getSize(originIndex);
                                historyItem.isDirty =
                                    !isRender || !(size.width === oldSize.width && size.height === oldSize.height);
                            }
                            if (historyItem.isDirty) break;
                        }
                    }
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IStageHistoryAdjustData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IStageHistoryAdjustData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onMessage: (message: [IStageHistoryAdjustData], isRedo, isUndo, isWrite) => {
                const { originIndex, units, oldStages, newStages } = message[0];
                this.service.frame.delActiveLayer();
                const update = (unit: SizeUnitType, stages: ILayerProps[][]) => {
                    for (let i = 0; i < stages.length; i++) {
                        this.service.frame.batchUpdateLayer(stages[i], false);
                    }
                    // this.service.frame.rerender(originIndex);
                    this.service.editor.setUnit(unit);
                };
                if (isUndo) {
                    update(units.targetUnit, oldStages);
                }
                if (isRedo || isWrite) {
                    update(units.destUnit, newStages);
                }
            },
        });
    }

    /** 舞台通用历史, insert, clone, create, delete, replace, swap */
    private registerBaseHistory() {
        this.service.history.register(historyBaseType, {
            owner: 'framework/stage',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IStageHistoryBaseData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { oldIds, oldIndex, newIds, newIndex, stageTypes } = historyItem.data.data;
                this.service.frame.delActiveLayer();
                if (isUndo) {
                    this.service.frame.restoreFrame(oldIndex, oldIds);
                    // 触发舞台选中事件， 业务(如页面组件)需要监听选择的变化更新 UI
                    const selection = [oldIndex];
                    this.service.frame.setFrameSelection(selection);
                }
                if (isRedo) {
                    this.service.frame.restoreFrame(newIndex, newIds);
                    // 触发舞台选中事件， 业务(如页面组件)需要监听选择的变化更新 UI
                    const selection = [newIndex];
                    this.service.frame.setFrameSelection(selection);
                }
                if (stageTypes) {
                    if (isUndo) {
                        this.service.edgelessCanvas.setRenderMode(stageTypes.oldStageType);
                        // this.service.editor.setCanvasType(stageTypes.oldStageType as any);
                    }
                    if (isRedo) {
                        this.service.edgelessCanvas.setRenderMode(stageTypes.newStageType);
                        // this.service.editor.setCanvasType(stageTypes.newStageType as any);
                    }
                }
                this.service.edgelessCanvas.requestRenderAll();
            },
            onAfterWrite: (historyItem: IHistoryItem<IStageHistoryBaseData>) => {
                const insertData = historyItem.data.data.insertData;
                if (insertData) {
                    this.service.history.realEdit.insertStageEnd(insertData.insertId);
                }
                return {
                    message: [historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IStageHistoryBaseData>) => {
                delete historyItem.data.data.insertData;
                delete historyItem.data.data.delData;
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IStageHistoryBaseData>, isRedo, isUndo, store) => {
                const { oldIds, oldIndex, newIds, newIndex, insertData, isReplaceActive } = historyItem.data.data;
                console.log('ot undo', cloneDeep(historyItem.data.data));

                const compareArrays = (oldIds: number[], newIds: number[]): boolean => {
                    if (oldIds.length !== newIds.length) {
                        return false;
                    }
                    const oldSet = new Set(oldIds);
                    const newSet = new Set(newIds);
                    return oldIds.every((id) => newSet.has(id)) && newIds.every((id) => oldSet.has(id));
                };
                const currentIds = [...this.service.frame.getRenderFrameIds()];
                if (insertData) {
                    // 新增画布
                    const { originIndexs } = insertData;
                    if (isUndo) {
                        store.set(historyItem.cursor, [...currentIds]);
                        if (isReplaceActive) {
                            const del = difference(oldIds, newIds);
                            if (isNumber(del[0]) && isNumber(originIndexs[0])) {
                                for (let i = 0; i < currentIds.length; i++) {
                                    if (currentIds[i] === originIndexs[0]) {
                                        currentIds[i] = del[0];
                                        break;
                                    }
                                }
                            }
                        }
                        pullAll(currentIds, originIndexs);
                        if (!currentIds.length) {
                            currentIds.push(...oldIds);
                        }
                        historyItem.data.data.oldIds = [...currentIds];
                        historyItem.data.data.oldIndex = clamp(oldIndex, 0, currentIds.length - 1);
                    }
                    if (isRedo) {
                        const beforeData = store.get(historyItem.cursor);
                        if (!beforeData) {
                            historyItem.isDirty = true;
                            return;
                        }
                        historyItem.data.data.newIds = [...beforeData];
                        historyItem.data.data.newIndex = clamp(newIndex, 0, beforeData.length - 1);
                    }
                } else {
                    // 删除画布\调整画布顺序
                    if (isUndo) {
                        historyItem.isDirty = !compareArrays(currentIds, newIds);
                        store.set(historyItem.cursor, {
                            isDirty: historyItem.isDirty,
                        });
                    }
                    if (isRedo) {
                        const beforeData = store.get(historyItem.cursor);
                        if (!beforeData) {
                            historyItem.isDirty = true;
                            return;
                        }
                        historyItem.isDirty = beforeData.isDirty;
                        if (!historyItem.isDirty) {
                            historyItem.isDirty = !compareArrays(currentIds, oldIds);
                        }
                    }
                }
            },
            onMessage: (message: [IStageHistoryBaseData], isRedo, isUndo, isWrite) => {
                const { oldIds, oldIndex, newIds, newIndex, stageTypes, insertData } = message[0];
                if (stageTypes) {
                    if (isUndo) {
                        this.service.edgelessCanvas.setRenderMode(stageTypes.oldStageType);
                        // this.service.editor.setCanvasType(stageTypes.oldStageType as any);
                    }
                    if (isRedo || isWrite) {
                        this.service.edgelessCanvas.setRenderMode(stageTypes.newStageType);
                        // this.service.editor.setCanvasType(stageTypes.newStageType as any);
                    }
                }
                if (isWrite && insertData) {
                    const { insertId, originIndexs, extraInfos, stages } = insertData;
                    this.service.frame.addExtraInfos(extraInfos, originIndexs);
                    stages.forEach((layers, index) => {
                        const originIndex = originIndexs[index];
                        layers.forEach((layer) => {
                            this.service.frame.addOrUpdateLayer(layer, originIndex);
                        });
                    });
                    const originIndex = this.service.frame.getActiveFrameId();
                    let restoreIndex = newIds.indexOf(originIndex);
                    if (restoreIndex === -1) {
                        restoreIndex = newIndex;
                        this.service.frame.delActiveLayer();
                    }
                    this.service.frame.restoreFrame(restoreIndex, newIds);
                    this.service.history.realEdit.insertStageEnd(insertId);
                } else {
                    if (isUndo) {
                        const originIndex = this.service.frame.getActiveFrameId();
                        let restoreIndex = oldIds.indexOf(originIndex);
                        if (restoreIndex === -1) {
                            restoreIndex = oldIndex;
                            this.service.frame.delActiveLayer();
                        }
                        this.service.frame.restoreFrame(restoreIndex, oldIds);
                        // 触发舞台选中事件， 业务(如页面组件)需要监听选择的变化更新 UI
                        const selection = [restoreIndex];
                        this.service.frame.setFrameSelection(selection);
                    }
                    if (isRedo || isWrite) {
                        const originIndex = this.service.frame.getActiveFrameId();
                        let restoreIndex = newIds.indexOf(originIndex);
                        if (restoreIndex === -1) {
                            restoreIndex = newIndex;
                            this.service.frame.delActiveLayer();
                        }
                        this.service.frame.restoreFrame(restoreIndex, newIds);
                        // 触发舞台选中事件， 业务(如页面组件)需要监听选择的变化更新 UI
                        const selection = [restoreIndex];
                        this.service.frame.setFrameSelection(selection);
                    }
                }
                this.service.edgelessCanvas.requestRenderAll();
            },
        });
    }
}
